/**
 * @fileoverview Create Escalation Modal Component
 * @module components/modals/CreateEscalationModal
 * @description Modal for L9 users to create or edit a member escalation from
 * the Member Outreach member page. Escalation reason options
 * are dependent on the selected escalation type, and a narrative description
 * is always required (including, and especially, when "Other" is selected).
 * Each escalation is a single mutable record - editing it updates it in
 * place.
 */

import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Typography,
  Select,
  MenuItem,
  TextField,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import dayjs from "dayjs";
import CloseIcon from "@mui/icons-material/Close";
import {
  ESCALATION_TYPES,
  ESCALATION_REASONS_BY_TYPE,
  TERMINAL_ESCALATION_STATUSES,
  RESOLUTION_LOCKED_STATUSES,
  ESCALATION_CONTACT_TYPES,
  CONTACT_STATUSES,
  ESCALATION_PRIORITIES,
  CANCELLATION_REASONS,
} from "../../constants/escalation";
import {
  fetchL9Users,
  createContactAttempt,
  fetchContactAttempts,
} from "../../api/escalation";
import { getCurrentUser } from "../../utils/auth";
import ContactAttemptModal from "./ContactAttemptModal";
import ContactLogsModal from "./ContactLogsModal";

const fieldLabelSx = {
  fontWeight: 500,
  fontSize: "14px",
  lineHeight: "35px",
  color: "#403C6F",
};

// Smaller than fieldLabelSx (14px) - these sit beside the Contact Status
// label as secondary actions, not a field label of their own.
const contactLinkSx = {
  p: 0,
  minWidth: 0,
  textTransform: "none",
  color: "#403C6F",
  fontWeight: 600,
  fontSize: "12px",
};

const selectSx = (hasError) => ({
  height: "45px",
  background: "#fff",
  borderRadius: "8px",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: hasError ? "#f44336" : "#E0E0E0",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: hasError ? "#f44336" : "#BDBDBD",
  },
});

const FieldLabel = ({ children, required = false }) => (
  <Typography sx={fieldLabelSx}>
    {children}
    {required && (
      <Box component="span" sx={{ color: "#f44336" }}>
        {" *"}
      </Box>
    )}
  </Typography>
);

FieldLabel.propTypes = {
  children: PropTypes.node,
  required: PropTypes.bool,
};

const FieldError = ({ message }) => {
  if (!message) return null;
  return (
    <Typography sx={{ color: "#f44336", fontSize: "12px", mt: "4px" }}>
      {message}
    </Typography>
  );
};

FieldError.propTypes = {
  message: PropTypes.string,
};

// contactAt is a single backend string, but ContactAttemptModal keeps
// separate Date and Time inputs - this combines them at the boundary so the
// popup's UI stays unaware of the wire format.
const combineDateTime = (date, time) => {
  if (!date) return "";
  return time ? `${date}T${time}` : date;
};

// Today's date as a local "YYYY-MM-DD" string, matching the <input
// type="date"> value format - used both as that input's min attribute and to
// validate the follow-up date isn't in the past.
const todayDateString = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

const getCurrentUserOwner = () => {
  const currentUser = getCurrentUser();
  const id = currentUser?.id ?? currentUser?.employeeId ?? "";
  const name =
    `${currentUser?.firstName || ""} ${currentUser?.lastName || ""}`.trim() ||
    currentUser?.emailId ||
    currentUser?.email ||
    "You";
  return { id, name };
};

const buildInitialFields = (initialData, currentOwner) => ({
  escalationType: initialData?.escalationType || "",
  escalationReason: initialData?.escalationReason || "",
  description: initialData?.description || "",
  ownerId: initialData?.ownerId ?? currentOwner.id,
  status: initialData?.status || "Open",
  priority: initialData?.priority || "Medium",
  followup: initialData?.followup || "",
  followUpNeeded: Boolean(initialData?.followUpNeeded),
  followUpDate: initialData?.followUpDate || "",
  resolution: initialData?.resolution || "",
  cancellationReason: initialData?.cancellationReason || "",
  contactType: initialData?.contactType || "",
  contactStatus: initialData?.contactStatus || "",
});

const DIRTY_COMPARE_KEYS = [
  "escalationType",
  "escalationReason",
  "description",
  "status",
  "priority",
  "followup",
  "followUpNeeded",
  "followUpDate",
  "resolution",
  "cancellationReason",
  "contactType",
  "contactStatus",
];

const hasFieldChanges = (baseline, current) =>
  !!baseline &&
  (String(current.ownerId) !== String(baseline.ownerId) ||
    DIRTY_COMPARE_KEYS.some((key) => current[key] !== baseline[key]));

const isEscalationFormValid = ({
  escalationType,
  escalationReason,
  description,
  followUpNeeded,
  followUpDate,
  requiresResolution,
  resolution,
  requiresCancellationReason,
  cancellationReason,
}) =>
  Boolean(escalationType) &&
  Boolean(escalationReason) &&
  Boolean(description.trim()) &&
  (!followUpNeeded ||
    (Boolean(followUpDate) && followUpDate >= todayDateString())) &&
  (!requiresResolution || Boolean(resolution.trim())) &&
  (!requiresCancellationReason || Boolean(cancellationReason));

const getRequiredError = (applies, isMissing, message) =>
  applies && isMissing ? message : "";

const CreateEscalationModal = ({
  open,
  onClose,
  onSave,
  initialData,
  readOnly = false,
  memberId,
}) => {
  const isEdit = Boolean(initialData);
  const [escalationType, setEscalationType] = useState("");
  const [escalationReason, setEscalationReason] = useState("");
  const [description, setDescription] = useState("");
  const [ownerId, setOwnerId] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [status, setStatus] = useState("Open");
  const [priority, setPriority] = useState("Medium");
  const [followup, setFollowup] = useState("");
  const [followUpNeeded, setFollowUpNeeded] = useState(false);
  const [followUpDate, setFollowUpDate] = useState("");
  const [resolution, setResolution] = useState("");
  const [cancellationReason, setCancellationReason] = useState("");
  const [contactType, setContactType] = useState("");
  const [contactStatus, setContactStatus] = useState("");
  const [isContactAttemptModalOpen, setIsContactAttemptModalOpen] =
    useState(false);
  const [isContactLogsModalOpen, setIsContactLogsModalOpen] = useState(false);
  const [contactAttempts, setContactAttempts] = useState([]);
  const [isSavingContactAttempt, setIsSavingContactAttempt] = useState(false);
  const [contactAttemptError, setContactAttemptError] = useState("");
  // While creating (no escalation id yet to attach a contact attempt to),
  // ContactAttemptModal's input is staged here (in memory, any number of
  // attempts) instead of posted immediately - see handleSubmit, which sends
  // them along as pendingContactAttempts for the parent to save right after
  // the escalation itself is created.
  const [draftContactAttempts, setDraftContactAttempts] = useState([]);
  const [l9Users, setL9Users] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [submitError, setSubmitError] = useState("");
  // Snapshot of the escalation's own fields as loaded, to tell whether Save
  // has anything to do while editing. Logging a contact attempt via the
  // "Contact" link saves immediately through its own API call and never
  // touches any of these fields, so it deliberately isn't "a change" here -
  // Save stays disabled until something in this snapshot itself diverges.
  const initialFieldsRef = useRef(null);

  useEffect(() => {
    if (open && l9Users.length === 0) {
      fetchL9Users()
        .then(setL9Users)
        .catch(() => setL9Users([]));
    }
  }, [open, l9Users.length]);

  useEffect(() => {
    if (open) {
      const currentOwner = getCurrentUserOwner();
      const fields = buildInitialFields(initialData, currentOwner);
      setEscalationType(fields.escalationType);
      setEscalationReason(fields.escalationReason);
      setDescription(fields.description);
      setOwnerId(fields.ownerId);
      setOwnerName(initialData?.ownerName || currentOwner.name);
      setStatus(fields.status);
      setPriority(fields.priority);
      setFollowup(fields.followup);
      setFollowUpNeeded(fields.followUpNeeded);
      setFollowUpDate(fields.followUpDate);
      setResolution(fields.resolution);
      setCancellationReason(fields.cancellationReason);
      setContactType(fields.contactType);
      setContactStatus(fields.contactStatus);
      setSubmitted(false);
      setIsSaving(false);
      setSubmitError("");
      setContactAttemptError("");
      setDraftContactAttempts([]);
      initialFieldsRef.current = fields;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialData]);

  // Contact attempts are a separate sub-resource keyed off the escalation's
  // real id (escalationId, not id - see mapEscalationResponse for why those
  // differ on a historical version). Fetched whenever an existing escalation
  // is opened so the "Contact Logs" link's visibility (hidden until at least
  // one attempt exists) is known without waiting for that link to be clicked.
  useEffect(() => {
    if (open && initialData?.escalationId) {
      fetchContactAttempts(memberId, initialData.escalationId)
        .then(setContactAttempts)
        .catch((error) => {
          console.error("Failed to fetch contact attempts", error);
          setContactAttempts([]);
        });
    } else {
      setContactAttempts([]);
    }
  }, [open, initialData, memberId]);

  const reasonOptions = ESCALATION_REASONS_BY_TYPE[escalationType] || [];

  // A new escalation only ever starts Open; editing can move it to a
  // terminal state directly (In Review/Pending Information/Closed aren't
  // offered). If the record already sits in some other legacy status, that
  // value is kept selectable so the dropdown still displays it correctly -
  // it just can't be chosen for anything that didn't already have it.
  const statusOptions = (() => {
    const base = isEdit ? ["Open", "Resolved", "Canceled"] : ["Open"];
    const original = initialData?.status;
    return original && !base.includes(original) ? [...base, original] : base;
  })();

  // Based on the status the record already had when the modal opened, not
  // the live status state - otherwise picking Resolved/Canceled would lock
  // the dropdown before there's ever a chance to change it back, with
  // nothing saved yet. Once a save actually lands in a terminal status, the
  // next time this opens, initialData.status reflects that and locks it.
  const isStatusLocked = TERMINAL_ESCALATION_STATUSES.includes(
    initialData?.status || "",
  );
  const requiresResolution = status === "Resolved" || status === "Closed";
  // Canceled has its own reason field instead - Resolution is hidden for it.
  const showResolutionField = requiresResolution;
  const requiresCancellationReason = status === "Canceled";
  // Based on the status the record already had when the modal opened, not
  // the live status state - otherwise switching the dropdown to Resolved
  // would lock the field before there's ever a chance to type a resolution.
  const isResolutionLocked = RESOLUTION_LOCKED_STATUSES.includes(
    initialData?.status || "",
  );
  // Cancellation reason locks under the same rule as Resolution - once the
  // record was already Canceled on open, the reason shouldn't change.
  const isCancellationReasonLocked = isResolutionLocked;

  // Follow-up date only makes sense once follow-up is flagged as needed, and
  // becomes required at that point. The follow-up notes textarea is never
  // shown while creating (there's nothing to follow up on yet) - while
  // editing, it only appears once a follow-up is both needed and dated, i.e.
  // once there's an actual follow-up to write notes against.
  const showFollowUpDate = followUpNeeded;
  const showFollowupText = isEdit && followUpNeeded && Boolean(followUpDate);

  // Whether every currently-required field has a value - gates Save on both
  // create and edit. Escalation type/reason/description are always required;
  // the rest only become required once their triggering condition applies.
  const isFormValid = isEscalationFormValid({
    escalationType,
    escalationReason,
    description,
    followUpNeeded,
    followUpDate,
    requiresResolution,
    resolution,
    requiresCancellationReason,
    cancellationReason,
  });

  // Whether anything on the escalation itself differs from what it loaded
  // with - gates Save while editing (see initialFieldsRef for why logging a
  // contact attempt alone doesn't count).
  const baseline = initialFieldsRef.current;
  const isDirty = hasFieldChanges(baseline, {
    escalationType,
    escalationReason,
    description,
    ownerId,
    status,
    priority,
    followup,
    followUpNeeded,
    followUpDate,
    resolution,
    cancellationReason,
    contactType,
    contactStatus,
  });

  const typeError = getRequiredError(
    submitted,
    !escalationType,
    "Escalation type is required",
  );
  const reasonError = getRequiredError(
    submitted,
    !escalationReason,
    "Escalation reason is required",
  );
  const descriptionError = getRequiredError(
    submitted,
    !description.trim(),
    "Description is required",
  );
  const followUpDateError = (() => {
    if (!submitted || !followUpNeeded) return "";
    if (!followUpDate) return "Follow-up date is required";
    if (followUpDate < todayDateString())
      return "Follow-up date cannot be in the past";
    return "";
  })();
  const resolutionError = getRequiredError(
    submitted && requiresResolution,
    !resolution.trim(),
    "Resolution is required",
  );
  const cancellationReasonError = getRequiredError(
    submitted && requiresCancellationReason,
    !cancellationReason,
    "Cancellation reason is required",
  );

  const handleTypeChange = (event) => {
    const value = event.target.value;
    setEscalationType(value);
    // "Other" has no reason list of its own - it always resolves to "Other"
    // (see ESCALATION_REASONS_BY_TYPE) - so the dropdown is hidden and this
    // is set directly instead of leaving it for the user to pick.
    setEscalationReason(value === "Other" ? "Other" : "");
  };

  const handleOwnerChange = (event) => {
    const id = event.target.value;
    const user = l9Users.find((u) => String(u.id) === String(id));
    setOwnerId(id);
    setOwnerName(user?.name || ownerName);
  };

  const handleContactTypeChange = (event) => {
    const value = event.target.value;
    setContactType(value);
    setContactStatus("");
  };

  // Picking a Contact Status never opens ContactAttemptModal itself - that's
  // only ever opened via the explicit "Contact" link next to Escalation
  // Contact, regardless of which status (including "Contact attempted") is
  // selected here.
  const handleContactStatusChange = (event) => {
    setContactStatus(event.target.value);
  };

  const handleContactAttemptSave = async ({
    date,
    time,
    contactedParty,
    method,
  }) => {
    // No escalation id yet while creating - stage it instead of posting; see
    // handleSubmit/pendingContactAttempts for where these actually get saved.
    if (!isEdit) {
      setDraftContactAttempts((prev) => [
        ...prev,
        {
          contactAt: combineDateTime(date, time),
          contactParty: contactedParty,
          contactMethod: method,
        },
      ]);
      setIsContactAttemptModalOpen(false);
      return;
    }

    setContactAttemptError("");
    setIsSavingContactAttempt(true);
    try {
      await createContactAttempt(memberId, initialData.escalationId, {
        contactAt: combineDateTime(date, time),
        contactParty: contactedParty,
        contactMethod: method,
      });
      setContactAttempts(
        await fetchContactAttempts(memberId, initialData.escalationId),
      );
      setIsContactAttemptModalOpen(false);
    } catch (error) {
      setContactAttemptError(
        error instanceof Error
          ? error.message
          : "Failed to save contact attempt. Please try again.",
      );
    } finally {
      setIsSavingContactAttempt(false);
    }
  };

  const handleOpenContactLogs = async () => {
    // Nothing is persisted yet while creating - the log is just the
    // in-memory drafts.
    if (!isEdit) {
      setIsContactLogsModalOpen(true);
      return;
    }
    try {
      setContactAttempts(
        await fetchContactAttempts(memberId, initialData.escalationId),
      );
    } catch (error) {
      console.error("Failed to fetch contact attempts", error);
    }
    setIsContactLogsModalOpen(true);
  };

  const handleSubmit = async () => {
    setSubmitted(true);
    setSubmitError("");

    if (!isFormValid) {
      return;
    }

    try {
      setIsSaving(true);
      const payload = {
        escalationType,
        escalationReason,
        description: description.trim(),
        ownerId,
        ownerName,
        status,
        priority,
        // Unchecking Follow-up Needed clears both the date and the notes -
        // there's nothing to follow up on, so nothing to schedule or narrate.
        followup: followUpNeeded ? followup.trim() : "",
        followUpNeeded,
        followUpDate: followUpNeeded ? followUpDate : "",
        resolution: showResolutionField ? resolution.trim() : "",
        cancellationReason: requiresCancellationReason
          ? cancellationReason
          : "",
        contactType,
        contactStatus,
        // Only ever set while creating (see handleContactAttemptSave) - the
        // parent saves this as a second call once the escalation this needs
        // an id from has actually been created.
        pendingContactAttempts: draftContactAttempts,
      };
      await onSave(payload);
    } catch (error) {
      setSubmitError(
        error instanceof Error
          ? error.message
          : "Failed to save escalation. Please try again.",
      );
      setIsSaving(false);
    }
  };

  if (!open) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        keepMounted={false}
        maxWidth={false}
        slotProps={{
          paper: {
            sx: {
              width: { xs: "95%", md: "1100px" },
              maxWidth: "90vw",
              borderRadius: "16px",
              padding: { xs: "16px", md: "24px" },
              backgroundColor: "#F7F8FB",
              boxShadow: "none",
              border: "none",
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            p: 0,
            m: 0,
            width: "100%",
            height: "40px",
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "20px",
              lineHeight: "30px",
              color: "#000000",
            }}
          >
            {(() => {
              if (readOnly) return "View Escalation";
              return isEdit ? "Edit Escalation" : "Create Escalation";
            })()}
          </Typography>
          <IconButton onClick={onClose} aria-label="Close escalation dialog">
            <CloseIcon sx={{ color: "#7F7C9F" }} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ padding: 0, mt: 2 }}>
          <Box>
            {/* Short fields (dropdowns, single-line date) - two columns on
                sm+ screens, paired row by row so Priority lines up with
                Escalation Reason, Owner with Escalation Contact, and
                Follow-up Needed with Contact Status. Cancellation Reason is
                the one field that grows unpredictably (locked-notice text,
                errors), so it spans both columns as its own row right after
                Status/Escalation Type instead of being paired with anything -
                that keeps every other row's height (and alignment) unaffected
                by it. */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                columnGap: 3,
                rowGap: 2,
              }}
            >
              <Box>
                <Typography sx={fieldLabelSx}>Status</Typography>
                <Select
                  fullWidth
                  size="small"
                  disabled={readOnly || isStatusLocked}
                  value={status}
                  onChange={(event) => setStatus(event.target.value)}
                  sx={selectSx(false)}
                >
                  {statusOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
                {isStatusLocked && (
                  <Typography
                    sx={{ color: "#7F7C9F", fontSize: "12px", mt: "4px" }}
                  >
                    This escalation is {status.toLowerCase()} and its status can
                    no longer be changed.
                  </Typography>
                )}
              </Box>

              <Box>
                <FieldLabel required>Escalation Type</FieldLabel>
                <Select
                  fullWidth
                  displayEmpty
                  size="small"
                  disabled={readOnly || isEdit}
                  value={escalationType}
                  onChange={handleTypeChange}
                  renderValue={(selected) =>
                    selected || "Select Escalation Type"
                  }
                  sx={selectSx(Boolean(typeError))}
                >
                  {ESCALATION_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
                <FieldError message={typeError} />
              </Box>

              {requiresCancellationReason && (
                <Box sx={{ gridColumn: { xs: "1", sm: "1 / -1" } }}>
                  <FieldLabel required>Cancellation Reason</FieldLabel>
                  <Select
                    fullWidth
                    displayEmpty
                    size="small"
                    disabled={readOnly || isCancellationReasonLocked}
                    value={cancellationReason}
                    onChange={(event) =>
                      setCancellationReason(event.target.value)
                    }
                    renderValue={(selected) =>
                      selected || "Select Cancellation Reason"
                    }
                    sx={selectSx(Boolean(cancellationReasonError))}
                  >
                    {CANCELLATION_REASONS.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                  <FieldError message={cancellationReasonError} />
                  {isCancellationReasonLocked && !readOnly && (
                    <Typography
                      sx={{ color: "#7F7C9F", fontSize: "12px", mt: "4px" }}
                    >
                      This escalation is canceled and its cancellation reason
                      can no longer be changed.
                    </Typography>
                  )}
                </Box>
              )}

              {escalationType !== "Other" && (
                <Box>
                  <FieldLabel required>Escalation Reason</FieldLabel>
                  <Select
                    fullWidth
                    displayEmpty
                    size="small"
                    disabled={readOnly || isEdit || !escalationType}
                    value={escalationReason}
                    onChange={(event) =>
                      setEscalationReason(event.target.value)
                    }
                    renderValue={(selected) =>
                      selected || "Select Escalation Reason"
                    }
                    sx={{
                      ...selectSx(Boolean(reasonError)),
                      ...(!escalationType
                        ? { backgroundColor: "#EFEFEF" }
                        : {}),
                    }}
                  >
                    {reasonOptions.map((reason) => (
                      <MenuItem key={reason} value={reason}>
                        {reason}
                      </MenuItem>
                    ))}
                  </Select>
                  <FieldError message={reasonError} />
                </Box>
              )}

              <Box>
                <Typography sx={fieldLabelSx}>Priority</Typography>
                <Select
                  fullWidth
                  size="small"
                  disabled={readOnly}
                  value={priority}
                  onChange={(event) => setPriority(event.target.value)}
                  sx={selectSx(false)}
                >
                  {ESCALATION_PRIORITIES.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </Box>

              <Box>
                <Typography sx={fieldLabelSx}>Owner</Typography>
                <Select
                  fullWidth
                  displayEmpty
                  size="small"
                  disabled={readOnly}
                  value={ownerId}
                  onChange={handleOwnerChange}
                  renderValue={() => ownerName || "Select Owner"}
                  sx={selectSx(false)}
                >
                  {l9Users.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.name}
                    </MenuItem>
                  ))}
                </Select>
              </Box>

              <Box>
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Typography sx={fieldLabelSx}>Escalation Contact</Typography>
                  {contactType && !readOnly && (
                    <Button
                      onClick={() => setIsContactAttemptModalOpen(true)}
                      sx={{ ...contactLinkSx, textDecoration: "underline" }}
                    >
                      Contact
                    </Button>
                  )}
                  {(isEdit ? contactAttempts : draftContactAttempts).length >
                    0 && (
                    <Button
                      onClick={handleOpenContactLogs}
                      sx={{ ...contactLinkSx, textDecoration: "underline" }}
                    >
                      Log
                    </Button>
                  )}
                </Box>
                <Select
                  fullWidth
                  displayEmpty
                  size="small"
                  disabled={readOnly}
                  value={contactType}
                  onChange={handleContactTypeChange}
                  renderValue={(selected) =>
                    selected || "Select Contact (Optional)"
                  }
                  sx={selectSx(false)}
                >
                  {ESCALATION_CONTACT_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </Box>

              <Box sx={{ overflow: "visible" }}>
                <Typography sx={fieldLabelSx}>Follow-up Needed</Typography>
                <FormControlLabel
                  sx={{ ml: 0, overflow: "visible" }}
                  control={
                    <Checkbox
                      checked={followUpNeeded}
                      onChange={(event) =>
                        setFollowUpNeeded(event.target.checked)
                      }
                      disabled={readOnly}
                      sx={{
                        color: "#9F9DB6",
                        p: "4px",
                        mr: "4px",
                        "&.Mui-checked": {
                          color: "#403C6F !important",
                        },
                      }}
                    />
                  }
                  label="Follow-up needed"
                />
              </Box>

              {contactType && (
                <Box>
                  <Typography sx={fieldLabelSx}>Contact Status</Typography>
                  <Select
                    fullWidth
                    displayEmpty
                    size="small"
                    disabled={readOnly}
                    value={contactStatus}
                    onChange={handleContactStatusChange}
                    renderValue={(selected) =>
                      selected || "Select Contact Status"
                    }
                    sx={selectSx(false)}
                  >
                    {CONTACT_STATUSES.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              )}

              {showFollowUpDate && (
                <Box>
                  <FieldLabel required>Follow-up Date</FieldLabel>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DesktopDatePicker
                      value={followUpDate ? dayjs(followUpDate) : null}
                      onChange={(newValue) => {
                        if (newValue === null) {
                          setFollowUpDate("");
                        } else if (newValue.isValid()) {
                          setFollowUpDate(newValue.format("YYYY-MM-DD"));
                        }
                      }}
                      minDate={dayjs(todayDateString())}
                      disabled={readOnly}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: "small",
                          error: Boolean(followUpDateError),
                          sx: {
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                              backgroundColor: "#fff",
                              "& fieldset": {
                                borderColor: followUpDateError
                                  ? "#f44336"
                                  : "#E0E0E0",
                              },
                            },
                          },
                        },
                      }}
                    />
                  </LocalizationProvider>
                  <FieldError message={followUpDateError} />
                </Box>
              )}
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                mt: 3,
              }}
            >
              <Box>
                <FieldLabel required>Description</FieldLabel>
                <TextField
                  fullWidth
                  multiline
                  minRows={4}
                  placeholder="Enter Escalation Details..."
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  error={Boolean(descriptionError)}
                  disabled={readOnly || isEdit}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      backgroundColor: "#fff",
                      "& fieldset": {
                        borderColor: descriptionError ? "#f44336" : "#E0E0E0",
                      },
                    },
                  }}
                />
                <FieldError message={descriptionError} />
              </Box>

              {showFollowupText && (
                <Box>
                  <Typography sx={fieldLabelSx}>Follow-up</Typography>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    placeholder="Enter Follow-Up Notes Or Next Steps..."
                    value={followup}
                    onChange={(event) => setFollowup(event.target.value)}
                    disabled={readOnly}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        backgroundColor: "#fff",
                        "& fieldset": { borderColor: "#E0E0E0" },
                      },
                    }}
                  />
                </Box>
              )}

              {showResolutionField && (
                <Box>
                  <FieldLabel required>Resolution</FieldLabel>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    placeholder="Enter Resolution Details..."
                    value={resolution}
                    onChange={(event) => setResolution(event.target.value)}
                    error={Boolean(resolutionError)}
                    disabled={readOnly || isResolutionLocked}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        backgroundColor: "#fff",
                        "& fieldset": {
                          borderColor: resolutionError ? "#f44336" : "#E0E0E0",
                        },
                      },
                    }}
                  />
                  <FieldError message={resolutionError} />
                  {isResolutionLocked && !readOnly && (
                    <Typography
                      sx={{ color: "#7F7C9F", fontSize: "12px", mt: "4px" }}
                    >
                      This escalation is {status.toLowerCase()} and its
                      resolution can no longer be changed.
                    </Typography>
                  )}
                </Box>
              )}

              {submitted && !isFormValid && (
                <Typography sx={{ color: "#f44336", fontSize: "13px" }}>
                  Please complete all required fields before saving.
                </Typography>
              )}

              {submitError && (
                <Typography sx={{ color: "#f44336", fontSize: "13px" }}>
                  {submitError}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            justifyContent: "flex-end",
            alignItems: "center",
            gap: "10px",
            mt: 1,
            p: 0,
          }}
        >
          <Button
            onClick={onClose}
            sx={{
              color: "#403C6F",
              backgroundColor: "#FFFFFF",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "14px",
              px: 3,
              borderRadius: "52px",
              "&:hover": { backgroundColor: "rgba(64, 60, 111, 0.04)" },
            }}
          >
            {readOnly ? "Close" : "Cancel"}
          </Button>
          {!readOnly &&
            (() => {
              // Editing requires an actual change (logging a contact attempt
              // alone doesn't count - see isDirty); creating has no prior
              // state to compare against. Validity is NOT a gate here - the
              // button must stay clickable so handleSubmit can flip
              // `submitted` and surface each missing-field error below its
              // field (and the summary banner above) instead of silently
              // doing nothing.
              const isSaveDisabled = isSaving || (isEdit && !isDirty);
              return (
                <Button
                  onClick={handleSubmit}
                  variant="contained"
                  disabled={isSaveDisabled}
                  sx={{
                    backgroundColor: isSaveDisabled ? "#A0A0A0" : "#403C6F",
                    color: "#FFFFFF",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "14px",
                    px: 3,
                    borderRadius: "52px",
                    "&:hover": {
                      backgroundColor: isSaveDisabled ? "#A0A0A0" : "#4a4576",
                    },
                    "&:disabled": {
                      backgroundColor: "#D4D4D6",
                      color: "#403C6F",
                      cursor: "not-allowed",
                      opacity: 1,
                    },
                  }}
                >
                  {isSaving ? "Saving..." : "Save"}
                </Button>
              );
            })()}
        </DialogActions>
      </Dialog>
      <ContactAttemptModal
        open={isContactAttemptModalOpen}
        onClose={() => setIsContactAttemptModalOpen(false)}
        onSave={handleContactAttemptSave}
        defaultContactedParty={contactType}
        isSaving={isSavingContactAttempt}
        error={contactAttemptError}
      />
      <ContactLogsModal
        open={isContactLogsModalOpen}
        onClose={() => setIsContactLogsModalOpen(false)}
        contactLogs={isEdit ? contactAttempts : draftContactAttempts}
      />
    </>
  );
};

CreateEscalationModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  initialData: PropTypes.shape({
    escalationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    escalationType: PropTypes.string,
    escalationReason: PropTypes.string,
    description: PropTypes.string,
    ownerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    ownerName: PropTypes.string,
    status: PropTypes.string,
    priority: PropTypes.string,
    followup: PropTypes.string,
    followUpNeeded: PropTypes.bool,
    followUpDate: PropTypes.string,
    resolution: PropTypes.string,
    cancellationReason: PropTypes.string,
    contactType: PropTypes.string,
    contactStatus: PropTypes.string,
  }),
  readOnly: PropTypes.bool,
  memberId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default CreateEscalationModal;
